import React, { useEffect, useRef, useState } from 'react';
import propTypes from 'prop-types';
import Chart from 'chart.js/auto';
import dayjs from 'dayjs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import usePost from '../../../Hooks/usePost';
import styled from 'styled-components';
import { useAdvertisers } from '../../../Contexts/AdvertisersContext';

const StyledTableContainer = styled.div`
    margin-top: 40px;
`;
const ViewCompareGoogleAnalytics = ({
    campaignType,
    campaign1Id,
    campaign2Id,
    campaign3Id,
    campaign4Id,
    campaign5Id,
    campaign1TypeId,
    campaign2TypeId,
    campaign3TypeId,
    campaign4TypeId,
    campaign5TypeId,
}) => {
    const { currentAdvertiser } = useAdvertisers();
    const currentAdvertiserId = currentAdvertiser?.advertiserId;

    const chartContainerRefs = useRef([]);

    const [statData, setStatData] = useState({});

    const { post } = usePost('/GetAllCampaignStatsMultiple');

    const fetchStatData = async () => {
        const body = {
            advertiserId: currentAdvertiserId,
            campaigns: [],
        };

        const campaignData = [
            { id: campaign1Id, typeId: campaign1TypeId },
            { id: campaign2Id, typeId: campaign2TypeId },
            { id: campaign3Id, typeId: campaign3TypeId },
            { id: campaign4Id, typeId: campaign4TypeId },
            { id: campaign5Id, typeId: campaign5TypeId },
        ];

        campaignData.forEach((campaign) => {
            if (campaign.id !== null && campaign.typeId !== null) {
                body.campaigns.push({
                    campaignId: Number(campaign.id),
                    campaignTypeId: Number(campaign.typeId),
                });
            }
        });

        const allCampaignData = await post(body, 'POST');
        const { googleCampaignStats } = allCampaignData?.data;

        setStatData({ google: googleCampaignStats });
    };

    const { google } = { ...statData };

    useEffect(() => {
        fetchStatData();
    }, [currentAdvertiserId]);

    const totalRows = google;

    useEffect(() => {
        const chartContainers = chartContainerRefs.current;

        const totalImpressionsData = {
            labels: totalRows?.map((item) => item.campaignName),
            datasets: [
                {
                    label: totalRows?.map((item) => item.campaignName),
                    backgroundColor: [
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(39, 119, 29, 0.6)',
                        'rgba(119, 112, 29, 0.6)',
                        'rgba(189, 14, 163, 0.6)',
                    ],
                    borderColor: [
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(39, 119, 29, 0.6)',
                        'rgba(119, 112, 29, 0.6)',
                        'rgba(189, 14, 163, 0.6)',
                    ],
                    borderWidth: 1,
                    data: totalRows?.map((item) => item.totalImps),
                },
            ],
        };

        const dailyImpressionsData = {
            labels: totalRows?.map((item) => item.campaignName),
            datasets: [
                {
                    label: totalRows?.map((item) => item.campaignName),
                    backgroundColor: [
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(39, 119, 29, 0.6)',
                        'rgba(119, 112, 29, 0.6)',
                        'rgba(189, 14, 163, 0.6)',
                    ],
                    borderColor: [
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(39, 119, 29, 0.6)',
                        'rgba(119, 112, 29, 0.6)',
                        'rgba(189, 14, 163, 0.6)',
                    ],
                    borderWidth: 1,
                    data: totalRows?.map((item) => item.totalImps / item?.dayCount),
                },
            ],
        };

        const totalClicksData = {
            labels: totalRows?.map((item) => item.campaignName),
            datasets: [
                {
                    label: totalRows?.map((item) => item.campaignName),
                    backgroundColor: [
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(39, 119, 29, 0.6)',
                        'rgba(119, 112, 29, 0.6)',
                        'rgba(189, 14, 163, 0.6)',
                    ],
                    borderColor: [
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(39, 119, 29, 0.6)',
                        'rgba(119, 112, 29, 0.6)',
                        'rgba(189, 14, 163, 0.6)',
                    ],
                    borderWidth: 1,
                    data: totalRows?.map((item) => item.totalClicks),
                },
            ],
        };

        const dailyClicksData = {
            labels: totalRows?.map((item) => item.campaignName),
            datasets: [
                {
                    label: totalRows?.map((item) => item.campaignName),
                    backgroundColor: [
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(39, 119, 29, 0.6)',
                        'rgba(119, 112, 29, 0.6)',
                        'rgba(189, 14, 163, 0.6)',
                    ],
                    borderColor: [
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(39, 119, 29, 0.6)',
                        'rgba(119, 112, 29, 0.6)',
                        'rgba(189, 14, 163, 0.6)',
                    ],
                    borderWidth: 1,
                    data: totalRows?.map((item) => item.totalClicks / item?.dayCount),
                },
            ],
        };

        const totalCPCData = {
            labels: totalRows?.map((item) => item.campaignName),
            datasets: [
                {
                    label: totalRows?.map((item) => item.campaignName),
                    backgroundColor: [
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(39, 119, 29, 0.6)',
                        'rgba(119, 112, 29, 0.6)',
                        'rgba(189, 14, 163, 0.6)',
                    ],
                    borderColor: [
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(39, 119, 29, 0.6)',
                        'rgba(119, 112, 29, 0.6)',
                        'rgba(189, 14, 163, 0.6)',
                    ],
                    borderWidth: 1,
                    data: totalRows?.map((item) => item.totalSpend / item.totalClicks),
                },
            ],
        };

        const dailyCPCData = {
            labels: totalRows?.map((item) => item.campaignName),
            datasets: [
                {
                    label: totalRows?.map((item) => item.campaignName),
                    backgroundColor: [
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(39, 119, 29, 0.6)',
                        'rgba(119, 112, 29, 0.6)',
                        'rgba(189, 14, 163, 0.6)',
                    ],
                    borderColor: [
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(39, 119, 29, 0.6)',
                        'rgba(119, 112, 29, 0.6)',
                        'rgba(189, 14, 163, 0.6)',
                    ],
                    borderWidth: 1,
                    data: totalRows?.map(
                        (item) => item.totalSpend / item.totalClicks / item?.dayCount
                    ),
                },
            ],
        };

        const totalCTRData = {
            labels: totalRows?.map((item) => item.campaignName),
            datasets: [
                {
                    label: totalRows?.map((item) => item.campaignName),
                    backgroundColor: [
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(39, 119, 29, 0.6)',
                        'rgba(119, 112, 29, 0.6)',
                        'rgba(189, 14, 163, 0.6)',
                    ],
                    borderColor: [
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(39, 119, 29, 0.6)',
                        'rgba(119, 112, 29, 0.6)',
                        'rgba(189, 14, 163, 0.6)',
                    ],
                    borderWidth: 1,
                    data: totalRows?.map((item) =>
                        ((item.totalClicks / item.totalImps) * 100).toFixed(2)
                    ),
                },
            ],
        };

        const dailyCTRData = {
            labels: totalRows?.map((item) => item.campaignName),
            datasets: [
                {
                    label: totalRows?.map((item) => item.campaignName),
                    backgroundColor: [
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(39, 119, 29, 0.6)',
                        'rgba(119, 112, 29, 0.6)',
                        'rgba(189, 14, 163, 0.6)',
                    ],
                    borderColor: [
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(39, 119, 29, 0.6)',
                        'rgba(119, 112, 29, 0.6)',
                        'rgba(189, 14, 163, 0.6)',
                    ],
                    borderWidth: 1,
                    data: totalRows?.map(
                        (item) =>
                            ((item.totalClicks / item.totalImps) * 100).toFixed(2) / item?.dayCount
                    ),
                },
            ],
        };

        const totalBarChartImps = new Chart(chartContainers[0]?.getContext('2d'), {
            type: 'bar',
            data: totalImpressionsData,
            options: {
                plugins: {
                    legend: {
                        display: false,
                    },
                    scales: {
                        y: {
                            display: true,
                        },
                    },
                    title: {
                        display: true,
                        text: 'Total Impressions',
                        font: {
                            size: '30px',
                        },
                    },
                },
            },
        });

        const dailyBarChartImps = new Chart(chartContainers[1]?.getContext('2d'), {
            type: 'bar',
            data: dailyImpressionsData,
            options: {
                plugins: {
                    legend: {
                        display: false,
                    },
                    scales: {
                        y: {
                            display: true,
                        },
                    },
                    title: {
                        display: true,
                        text: 'Daily Average Impressions',
                        font: {
                            size: '30px',
                        },
                    },
                },
            },
        });

        const totalBarChartClicks = new Chart(chartContainers[2]?.getContext('2d'), {
            type: 'bar',
            data: totalClicksData,
            options: {
                plugins: {
                    legend: {
                        display: false,
                    },
                    scales: {
                        y: {
                            display: true,
                        },
                    },
                    title: {
                        display: true,
                        text: 'Total Clicks',
                        font: {
                            size: '30px',
                        },
                    },
                },
            },
        });
        const dailyBarChartClicks = new Chart(chartContainers[3]?.getContext('2d'), {
            type: 'bar',
            data: dailyClicksData,
            options: {
                plugins: {
                    legend: {
                        display: false,
                    },
                    scales: {
                        y: {
                            display: true,
                        },
                    },
                    title: {
                        display: true,
                        text: 'Daily Average Clicks',
                        font: {
                            size: '30px',
                        },
                    },
                },
            },
        });

        const totalBarChartCPC = new Chart(chartContainers[4]?.getContext('2d'), {
            type: 'bar',
            data: totalCPCData,
            options: {
                plugins: {
                    legend: {
                        display: false,
                    },
                    scales: {
                        y: {
                            display: true,
                        },
                    },
                    title: {
                        display: true,
                        text: 'Total CPC',
                        font: {
                            size: '30px',
                        },
                    },
                },
            },
        });

        const dailyBarChartCPC = new Chart(chartContainers[5]?.getContext('2d'), {
            type: 'bar',
            data: dailyCPCData,
            options: {
                plugins: {
                    legend: {
                        display: false,
                    },
                    scales: {
                        y: {
                            display: true,
                        },
                    },
                    title: {
                        display: true,
                        text: 'Daily Average CPC',
                        font: {
                            size: '30px',
                        },
                    },
                },
            },
        });
        const totalBarChartCTR = new Chart(chartContainers[6]?.getContext('2d'), {
            type: 'bar',
            data: totalCTRData,
            options: {
                plugins: {
                    legend: {
                        display: false,
                    },
                    scales: {
                        y: {
                            display: true,
                        },
                    },
                    title: {
                        display: true,
                        text: 'Total CTR',
                        font: {
                            size: '30px',
                        },
                    },
                },
            },
        });

        const dailyBarChartCTR = new Chart(chartContainers[7]?.getContext('2d'), {
            type: 'bar',
            data: dailyCTRData,
            options: {
                plugins: {
                    legend: {
                        display: false,
                    },
                    scales: {
                        y: {
                            display: true,
                        },
                    },
                    title: {
                        display: true,
                        text: 'Daily Average CTR',
                        font: {
                            size: '30px',
                        },
                    },
                },
            },
        });

        // Cleanup: Destroy charts when component unmounts
        return () => {
            totalBarChartImps.destroy();
            dailyBarChartImps.destroy();
            totalBarChartClicks.destroy();
            dailyBarChartClicks.destroy();
            totalBarChartCPC.destroy();
            dailyBarChartCPC.destroy();
            totalBarChartCTR.destroy();
            dailyBarChartCTR.destroy();
        };
    }, [statData]);

    return (
        <div
            style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                style={{
                    textAlign: 'center',
                    fontSize: '23px',
                    fontWeight: 'bold',
                    marginTop: '50px',
                }}
            >
                Analytics
            </div>

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: '120px',
                    flexDirection: 'column',
                }}
            >
                <StyledTableContainer>
                    <div>
                        <div
                            style={{
                                width: '100%',
                                maxWidth: '1500px',
                                display: 'table',
                                tableLayout: 'fixed',
                            }}
                        >
                            <TableContainer
                                style={{
                                    border: '1px solid black',
                                    borderRadius: '12px',
                                    backgroundColor: ' #fafbf7',
                                }}
                                component={Paper}
                            >
                                <Table aria-label="simple table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Campaign Name</TableCell>
                                            <TableCell align="right">Campaign Type</TableCell>
                                            <TableCell align="right">Campaign Range</TableCell>
                                            <TableCell align="right"># of Days</TableCell>
                                            <TableCell align="right">Total Impressions</TableCell>
                                            <TableCell align="right">Total Clicks</TableCell>
                                            <TableCell align="right">Total Budget</TableCell>
                                            <TableCell align="right">Total CPM</TableCell>
                                            <TableCell align="right">Total CTR</TableCell>
                                            <TableCell align="right">Total CPC</TableCell>
                                            <TableCell align="right">Updated By</TableCell>
                                            <TableCell align="right">Updated On</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {totalRows?.map((row, idx) => (
                                            <TableRow
                                                key={idx}
                                                sx={{
                                                    '&:last-child td, &:last-child th': {
                                                        border: 0,
                                                    },
                                                }}
                                            >
                                                <TableCell component="th" scope="row">
                                                    {row?.campaignName}
                                                </TableCell>
                                                <TableCell align="right">{campaignType}</TableCell>
                                                <TableCell align="right">
                                                    {`${dayjs(row?.startDate).format(
                                                        'MM/DD/YYYY'
                                                    )}- ${dayjs(row?.endDate).format(
                                                        'MM/DD/YYYY'
                                                    )}`}
                                                </TableCell>
                                                <TableCell align="right">{row?.dayCount}</TableCell>
                                                <TableCell align="right">
                                                    {row?.totalImps?.toLocaleString()}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row?.totalClicks?.toLocaleString()}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row?.budget?.toLocaleString('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    })}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row?.totalSpend && row?.totalImps
                                                        ? (
                                                              (row?.totalSpend / row?.totalImps) *
                                                              1000
                                                          ).toLocaleString()
                                                        : 0}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row?.totalClicks && row?.totalImps
                                                        ? (
                                                              (row?.totalClicks / row?.totalImps) *
                                                              100
                                                          ).toLocaleString()
                                                        : 0}
                                                    %
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row?.totalSpend && row?.totalClicks
                                                        ? (
                                                              row?.totalSpend / row?.totalClicks
                                                          )?.toLocaleString('en-US', {
                                                              style: 'currency',
                                                              currency: 'USD',
                                                          })
                                                        : 0}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row?.updatedBy}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {dayjs(row?.updatedOn).format('MM/DD/YYYY')}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    </div>
                </StyledTableContainer>
                {totalRows?.length === 0 ? (
                    <div
                        style={{
                            textAlign: 'center',
                            backgroundColor: 'white',
                        }}
                    >
                        There are no daily statistics for this campaign.
                    </div>
                ) : null}
            </div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    {Array.from({ length: 4 }, (_, rowIndex) => (
                        <div
                            key={rowIndex}
                            style={{
                                display: 'flex',
                            }}
                        >
                            {Array.from({ length: 2 }, (_, colIndex) => (
                                <div
                                    key={colIndex}
                                    style={{
                                        // flex: '1', // Equal width for both charts
                                        // padding: '10px', // Add some spacing between charts
                                        margin: '50px', // Add a small gap between charts
                                        width: '800px', // Increase the width of each chart container
                                        height: '400px', // Increase the width of each chart container
                                    }}
                                >
                                    <canvas
                                        ref={(el) =>
                                            (chartContainerRefs.current[rowIndex * 2 + colIndex] =
                                                el)
                                        }
                                        style={{
                                            maxWidth: '100%', // Make sure the canvas takes up the entire container
                                            maxHeight: '100%',
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

ViewCompareGoogleAnalytics.propTypes = {
    campaignType: propTypes.string,
    campaign1Id: propTypes.string,
    campaign2Id: propTypes.string,
    campaign3Id: propTypes.string,
    campaign4Id: propTypes.string,
    campaign5Id: propTypes.string,
    campaign1TypeId: propTypes.string,
    campaign2TypeId: propTypes.string,
    campaign3TypeId: propTypes.string,
    campaign4TypeId: propTypes.string,
    campaign5TypeId: propTypes.string,
};

export default ViewCompareGoogleAnalytics;
